import { getOrCreateVisitorId } from "./visitorId";
import { CampaignTracking } from "./CampaignTracking";

interface GtagEventParameters {
  [key: string]: string | number | boolean | undefined;
}

declare function gtag(
  command: "event",
  eventAction: string,
  eventParameters?: GtagEventParameters,
): void;

const eventsType: { [key: string]: string } = {
  FORM: "conversion",
};

const getEventType = (tagName: string) => {
  return eventsType[tagName] || "click";
};

/**
 * Enriches the gtag event parameters with global visitor and campaign tracking.
 */
const enrichEventParameters = (
  params: GtagEventParameters = {},
): GtagEventParameters => {
  const visitorId = getOrCreateVisitorId();
  const trackingData = CampaignTracking.getTrackingData() || {};
  return {
    ...params,
    ...(visitorId ? { af_visitor_id: visitorId } : {}),
    ...trackingData,
  };
};

const sendGtagEventFromDataset = (event: Event) => {
  const target = event.target as HTMLElement;
  const datasetElement = target.closest("[data-gtag-action]") as HTMLElement;

  if (!datasetElement) return;
  const eventAction = datasetElement.dataset.gtagAction;
  if (!eventAction) return;

  const eventLabel = datasetElement.dataset.gtagLabel;
  const eventCategory = datasetElement.dataset.gtagCategory;

  const eventParameters: GtagEventParameters = {};
  if (eventLabel) eventParameters.label = eventLabel;
  if (eventCategory) eventParameters.category = eventCategory;

  if (typeof gtag !== "function") return;
  gtag("event", eventAction, enrichEventParameters(eventParameters));
};

let isDropdownTrackingInitialized = false;
const trackMenuItemDropdown = (trackedSections: NodeList) => {
  if (isDropdownTrackingInitialized) return;

  const getDropdownItems = () => {
    return Array.from(trackedSections).filter((item) => {
      const element = item as HTMLElement;
      return (
        element.tagName === "BUTTON" &&
        element.dataset.gtagCategory === "Cabeçalho" &&
        element.dataset.gtagAction === "Clique Menu" &&
        element.closest("nav")
      );
    }) as HTMLElement[];
  };

  const handleDropdownClick = (event: Event) => {
    const element = event.currentTarget as HTMLElement;
    const closestElement = element.closest("li");
    if (!closestElement) return;

    setTimeout(() => {
      const submenus = closestElement.querySelectorAll("div.header-submenu li");
      submenus.forEach((submenu) => {
        submenu.removeEventListener("click", sendGtagEventFromDataset);
        submenu.addEventListener("click", sendGtagEventFromDataset);
      });
    });
  };

  const itemWithDropdown = getDropdownItems();
  if (!itemWithDropdown.length) return;

  isDropdownTrackingInitialized = true;
  itemWithDropdown.forEach((element) => {
    element.addEventListener("click", handleDropdownClick);
  });
};

export function sendGtagEvent(
  eventAction: string,
  eventParameters?: GtagEventParameters,
): void {
  if (typeof gtag !== "function") return;
  gtag("event", eventAction, enrichEventParameters(eventParameters));
}

export const initializeGtagEventListeners = () => {
  const trackedSections = document.querySelectorAll("[data-gtag-action]");
  trackMenuItemDropdown(trackedSections);

  trackedSections.forEach((section) => {
    const eventType = getEventType(section.tagName);
    section.removeEventListener(eventType, sendGtagEventFromDataset);
    section.addEventListener(eventType, sendGtagEventFromDataset);
  });

  return () => {
    trackedSections.forEach((section) => {
      section.removeEventListener(
        getEventType(section.tagName),
        sendGtagEventFromDataset,
      );
    });
  };
};
